import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import {
  listDevices as defaultList,
  revokeDevice as defaultRevoke,
  type DeviceListItemWire,
} from '../api/devicesApi'
import { parseUserAgent, type ParsedUserAgent } from './parseUserAgent'

/**
 * Trusted devices flow (Track E-3、Settings レビュー リスク B Medium)。
 *
 * - `loadDevices`: GET → wire 型を UI 用に整形 (UA parse 込み)
 * - `revoke(sessionId, { isCurrent })`: DELETE → 自身を revoke した場合は sessionStore.clear() で
 *   UI 側のロック画面に誘導する (refresh cookie 自体は server 側で次回 401)
 *
 * Error mapping (UI 側で messageKey に変換、CLAUDE.md §2 ルール 10):
 *   401  → 'unauthorized'   (再ログイン誘導)
 *   404  → 'not_found'      (既に revoke 済 — race condition、UI は無視 OK)
 *   429  → 'too_many_requests'
 *   *    → 'network'
 *   else → 'generic'
 */

export class DevicesError extends Error {
  public readonly code: 'unauthorized' | 'not_found' | 'too_many_requests' | 'network' | 'generic'

  public constructor(code: DevicesError['code']) {
    super(code)
    this.code = code
    this.name = 'DevicesError'
  }
}

export interface DevicesFlowDeps {
  list: typeof defaultList
  revoke: typeof defaultRevoke
}

const defaultDeps: DevicesFlowDeps = {
  list: defaultList,
  revoke: defaultRevoke,
}

/**
 * UI 用に整形した device エントリ。
 *
 * `parsedUa.display` は `parseUserAgent` の結果。`displayName` は i18n テンプレートで
 * 「Chrome / macOS (203.0.113.0/24)」のように組み立てる用途で caller が使う。
 */
export interface UiDevice {
  id: string
  isCurrent: boolean
  userAgent: string | null
  ipPrefix: string | null
  parsedUa: ParsedUserAgent
  createdAt: string
  lastUsedAt: string
}

const mapApiError = (err: unknown): DevicesError => {
  if (err instanceof ApiError) {
    if (err.status === 401) return new DevicesError('unauthorized')
    if (err.status === 404) return new DevicesError('not_found')
    if (err.status === 429) return new DevicesError('too_many_requests')

    return new DevicesError('network')
  }

  return new DevicesError('generic')
}

const toUi = (wire: DeviceListItemWire): UiDevice => ({
  id: wire.id,
  isCurrent: wire.isCurrent,
  userAgent: wire.userAgent,
  ipPrefix: wire.ipPrefix,
  parsedUa: parseUserAgent(wire.userAgent),
  createdAt: wire.createdAt,
  lastUsedAt: wire.lastUsedAt,
})

export const loadDevices = async (deps: DevicesFlowDeps = defaultDeps): Promise<UiDevice[]> => {
  try {
    const { devices } = await deps.list()

    return devices.map(toUi)
  } catch (err: unknown) {
    throw mapApiError(err)
  }
}

export interface RevokeOptions {
  /** UI から渡される (revoke 対象が currentDevice かどうか)。flow 内で確定する判定は別途。 */
  isCurrent: boolean
}

export const revokeDeviceFlow = async (
  sessionId: string,
  opts: RevokeOptions,
  deps: DevicesFlowDeps = defaultDeps,
): Promise<void> => {
  try {
    await deps.revoke(sessionId)
  } catch (err: unknown) {
    throw mapApiError(err)
  }

  if (opts.isCurrent) {
    // 自身を revoke した: server 側で refresh cookie が次回 401 で死ぬ。
    // client 側でも先に SK + material を zeroize して #login へ誘導する。
    sessionStore.clear()
  }
}
